import { cerrarContratoTx } from "../services/CotizacionService";

// Cierre de cotización (COTIZACION -> ASIGNACION). La lógica es dos fases:
//
// FASE 1 (transaccional, backend principal): un único POST /cerrarContrato que
// aplica en una sola transacción de BD la sincronización de detalles, los
// subproductos, el upsert del contrato, la reconstrucción de los períodos de
// facturación, el cambio de estado a ASIGNACIÓN (con días de aviso) y el plazo
// de firma. Ante cualquier fallo el backend hace rollback total; aquí se
// muestra el mensaje y NO se navega, de modo que el usuario puede reintentar
// sin quedar con datos a medias (el endpoint es idempotente para reintentos).
//
// FASE 2 (post-commit, contratos.service): registro del documento del contrato
// (plantilla + metadata). Es un paso recuperable: si falla, el cierre ya está
// comprometido y consistente; solo queda pendiente el documento y se avisa con
// un toast para reintentarlo.

export const ejecutarCierreContrato = async ({
  baseUrl,
  urlContratos,
  idCotizacion,
  idCliente,
  diasAviso,
  diasFirma,
  detallesPayload,
  navigate,
  alertFn = alert,
}) => {
  // FASE 1: cierre transaccional en el backend principal.
  let txResp;
  try {
    txResp = await cerrarContratoTx(baseUrl, {
      idCotizacion,
      idCliente,
      diasAviso,
      diasFirma,
      detalles: detallesPayload,
    });
  } catch (error) {
    alertFn(error?.message || "Error al cerrar el contrato");
    return { aborted: true };
  }

  const contrato = txResp?.contrato;
  if (!contrato || !contrato.id || !contrato.idEmpresa) {
    alertFn(
      "El cierre no devolvió los datos del contrato. Verifique el estado de la cotización antes de reintentar."
    );
    return { aborted: true };
  }

  // FASE 2: documento del contrato en contratos.service (post-commit). Un fallo
  // aquí no revierte el cierre: se registra en consola y se avisa por toast.
  let documentoOk = false;
  try {
    const plantillaResp = await fetch(
      `${urlContratos}getPlantillaContrato?idEmpresa=${contrato.idEmpresa}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      }
    );
    if (!plantillaResp.ok) {
      throw new Error("No se pudo obtener la plantilla de contrato");
    }
    const plantillaText = await plantillaResp.text();
    const idPlantilla = Number(plantillaText.trim());
    if (!Number.isFinite(idPlantilla) || idPlantilla <= 0) {
      throw new Error(`idPlantilla inválido: "${plantillaText}"`);
    }

    const metadataResp = await fetch(
      `${baseUrl}getMetadataByCotizacionId?idCotizacion=${idCotizacion}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
      }
    );
    if (!metadataResp.ok) {
      throw new Error("No se pudo obtener la metadata por cotización");
    }
    const metadataJson = await metadataResp.json();

    const payloadContratoDocumento = {
      idEmpresa: contrato.idEmpresa,
      idPlantilla,
      idContrato: contrato.id,
      idUsuario: Number(sessionStorage.getItem("usuario_id") || 0),
      idEstadoDocumento: 1,
      filename: `contrato ${idCotizacion}.docx`,
      metadataJson: metadataJson || {},
      bypass: true,
    };

    const guardarResp = await fetch(`${urlContratos}guardarMetadataDocumento`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payloadContratoDocumento),
      credentials: "include",
    });
    documentoOk = guardarResp.ok;
    if (!documentoOk) {
      console.error(
        `Error al registrar el documento del contrato (${guardarResp.status})`
      );
    }
  } catch (error) {
    console.error("Error al generar el documento del contrato:", error);
  }

  navigate("/asignacion", {
    state: {
      toast: [
        {
          severity: "success",
          summary: "Éxito",
          detail: "La cotización ha sido cerrada con éxito",
          life: 3000,
        },
        documentoOk
          ? {
              severity: "success",
              summary: "Éxito",
              detail: "El contrato se ha creado con éxito",
              life: 3000,
            }
          : {
              severity: "warn",
              summary: "Documento pendiente",
              detail:
                "El contrato quedó cerrado, pero no se pudo registrar el documento. Intente generarlo nuevamente.",
              life: 6000,
            },
      ],
    },
  });

  return { aborted: false };
};
